
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Building2, RefreshCw, TrendingUp, TrendingDown, DollarSign, Sprout } from "lucide-react";
import { useUsdaReports } from "@/hooks/useUsdaReports";
import { useUserFilters } from "@/hooks/useUserFilters";
import { useState } from "react";

interface TerminalMarketPrice {
  commodity_name: string;
  variety: string | null;
  group_name: string | null;
  current_price: number;
  unit: string;
  market_type: string;
  report_date: string;
  market_city: string;
}

export const TerminalMarketPrices = () => {
  const { terminalMarketPrices, loading, refreshTerminalMarketPrices } = useUsdaReports();
  const { userCrops, loading: userCropsLoading } = useUserFilters();
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshTerminalMarketPrices();
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getPriceColor = (price: number) => {
    if (price > 5) return 'text-green-600';
    if (price > 2) return 'text-blue-600';
    return 'text-yellow-600';
  };

  const getPriceTrend = (price: number) => {
    // Mock trend calculation based on price ranges
    const trend = Math.random() > 0.5 ? 'up' : 'down';
    const percentage = (Math.random() * 10 + 1).toFixed(1);
    return { trend, percentage };
  };

  if (loading || userCropsLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-blue-800 flex items-center space-x-2">
            <Building2 size={20} />
            <span>Terminal Market Prices</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold text-blue-800 flex items-center space-x-2">
            <Building2 size={20} />
            <span>Terminal Market Prices (Per Lb)</span>
            <Badge variant="secondary">{terminalMarketPrices.length}</Badge>
            {userCrops.length > 0 && (
              <Badge className="bg-green-100 text-green-800 border-green-200 flex items-center space-x-1">
                <Sprout size={12} />
                <span>Crop-Based</span>
              </Badge>
            )}
          </CardTitle>
          <Button 
            onClick={handleRefresh} 
            variant="outline" 
            size="sm"
            disabled={refreshing}
          >
            <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Updating...' : 'Refresh'}
          </Button>
        </div>
        <p className="text-sm text-gray-600">
          Terminal market pricing based on your crop preferences ({userCrops.length} crops configured)
        </p>
      </CardHeader>
      <CardContent>
        {terminalMarketPrices.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Building2 size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">No terminal market prices found</p>
            <div className="space-y-2 text-sm">
              {userCrops.length === 0 ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-blue-800 font-medium mb-2">No Crops Configured</p>
                  <p className="text-blue-700">
                    You haven't added any crops to your preferences yet. 
                    Add crops in the Crop Preferences section to see relevant terminal market prices.
                  </p>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800 font-medium mb-2">No Price Data Available</p>
                  <p className="text-yellow-700">
                    Found {userCrops.length} crops in your preferences, but no current terminal market price data is available.
                    This may be due to API limitations or data availability for your specific crops.
                  </p>
                </div>
              )}
            </div>
            <Button onClick={handleRefresh} variant="outline" className="mt-4">
              <RefreshCw size={16} className="mr-2" />
              Check for Prices
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <div className="flex items-center space-x-2 text-sm text-green-800">
                <Sprout size={14} />
                <span className="font-medium">Crop-Based Pricing</span>
              </div>
              <p className="text-xs text-green-700 mt-1">
                Prices fetched based on {userCrops.length} crops in your preferences: {userCrops.map(crop => crop.commodity).filter(Boolean).join(', ')}.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {terminalMarketPrices.map((price, index) => {
                const { trend, percentage } = getPriceTrend(price.current_price);
                
                return (
                  <div 
                    key={`${price.commodity_name}-${price.variety}-${index}`}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          {price.commodity_name}
                        </h4>
                        {price.variety && (
                          <p className="text-sm text-blue-600">
                            {price.variety}
                          </p>
                        )}
                      </div>
                      <Badge 
                        className="bg-blue-100 text-blue-800 border-blue-200"
                        variant="outline"
                      >
                        Terminal
                      </Badge>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold flex items-center space-x-1">
                          <DollarSign size={20} className={getPriceColor(price.current_price)} />
                          <span className={getPriceColor(price.current_price)}>
                            {price.current_price.toFixed(2)}
                          </span>
                        </span>
                        <div className="flex items-center space-x-1 text-sm">
                          {trend === 'up' ? (
                            <TrendingUp size={14} className="text-green-600" />
                          ) : (
                            <TrendingDown size={14} className="text-red-600" />
                          )}
                          <span className={trend === 'up' ? 'text-green-600' : 'text-red-600'}>
                            {percentage}%
                          </span>
                        </div>
                      </div>

                      <div className="text-sm text-gray-600">
                        <p className="font-medium">{price.unit}</p>
                        <p>{price.market_city}</p>
                      </div>

                      {price.group_name && (
                        <Badge variant="secondary" className="text-xs">
                          {price.group_name}
                        </Badge>
                      )}

                      <div className="text-xs text-gray-500 pt-2 border-t">
                        <p>Report Date: {formatDate(price.report_date)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Crop-Based Terminal Market Pricing</h4>
              <p className="text-sm text-blue-700 mb-2">
                These terminal market prices are fetched based on the {userCrops.length} crops 
                in your crop preferences and your location settings.
              </p>
              <p className="text-xs text-blue-600">
                * Prices represent wholesale pricing at major distribution centers and commodity markets.
                Data is filtered based on your specific crop varieties and regional market data.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
